import React from "react";
import { useRouter } from "next/router";
import { twMerge } from "tailwind-merge";
import {
  HeroUnit as PrimitiveHeroUnit,
  Skeleton,
} from "@gonoodle/gn-universe-ui";
import {
  PAGES,
  SECTIONS,
  HERO_UNIT_ITEM_TYPES,
} from "@gonoodle/gn-universe-analytics-schema/src/constants";
import { PlayIcon, DownloadIcon } from "@heroicons/react/outline";

import { CONTENT_TYPES } from "../../../../constants";
import { WithContentLink, Link } from "../../../../containers";
import { getUtmParamsFromQueryParams } from "../../../../utils/analytics";
import { getRoutePrefixByContentType } from "../../../../utils/route";
import { useDataLoader } from "../../index";

const getOriginalItemTitle = ({ contentType, content }) =>
  [CONTENT_TYPES.TAG, CONTENT_TYPES.CHANNEL, CONTENT_TYPES.TOPIC].includes(
    contentType,
  )
    ? content.name
    : content.title;

const PrimaryItemCardWithLink = WithContentLink(PrimitiveHeroUnit.PrimaryCard);
const SecondaryItemCardWithLink = WithContentLink(
  PrimitiveHeroUnit.SecondaryCard,
);

export default function HeroUnit({ fetchers }) {
  const router = useRouter();
  const [fetcher] = fetchers;
  const { data, isLoading } = useDataLoader(fetcher);
  const heroUnit = Array.isArray(data) ? data[0] : data;

  if (isLoading) {
    return <Skeleton className="h-[580px]" />;
  }

  const { primaryItem, secondaryItems } = heroUnit;

  const primaryItemLinkProps = {
    events: [
      {
        event: "Link Clicked",
        properties: {
          title: getOriginalItemTitle(primaryItem),
          sourceElement: HERO_UNIT_ITEM_TYPES.PRIMARY,
          sourceName: SECTIONS.HERO_UNIT,
          sourcePage: PAGES.HOME,
          sourcePageType: PAGES.HOME,
          url: heroUnit.url,
          ...getUtmParamsFromQueryParams(router.query),
        },
      },
    ],
  };

  return (
    <PrimitiveHeroUnit.Root
      textTheme={primaryItem.textTheme}
      className={twMerge(
        "bg-gray-800 rounded-lg lg:bg-transparent lg:rounded-none",
      )}
    >
      {[CONTENT_TYPES.CUSTOM_LINK].includes(primaryItem.contentType) ? (
        <Link
          {...primaryItemLinkProps}
          href={primaryItem.content.destinationUrl}
          className="w-full"
        >
          <PrimitiveHeroUnit.PrimaryCard
            title={primaryItem.title}
            description={primaryItem.description}
            media={{
              loopingVideo: primaryItem.loopingVideo,
              backgroundImage: primaryItem.backgroundImage,
            }}
            textTheme={primaryItem.textTheme}
            callToAction={primaryItem.ctaText}
          />
        </Link>
      ) : (
        <PrimaryItemCardWithLink
          {...primaryItemLinkProps}
          id={primaryItem.content.id}
          slug={primaryItem.content.slug}
          contentLinkAsBase={getRoutePrefixByContentType(
            primaryItem.contentType,
          )}
          contentLinkHrefBase={`${getRoutePrefixByContentType(
            primaryItem.contentType,
          )}/[...identity]`}
          linkParams={{
            sourceElement: HERO_UNIT_ITEM_TYPES.PRIMARY,
            sourceName: SECTIONS.HERO_UNIT,
            sourcePage: PAGES.HOME,
            sourcePageType: PAGES.HOME,
          }}
          title={primaryItem.title}
          description={primaryItem.description}
          media={{
            loopingVideo: primaryItem.loopingVideo,
            backgroundImage: primaryItem.backgroundImage,
          }}
          textTheme={primaryItem.textTheme}
          className="w-full"
          callToAction={
            <>
              {primaryItem.ctaText}
              {CONTENT_TYPES.VIDEO === primaryItem.contentType && (
                <PlayIcon className="h-input-icon ml-xs" />
              )}

              {CONTENT_TYPES.ACTIVITY === primaryItem.contentType && (
                <DownloadIcon className="h-input-icon ml-xs" />
              )}
            </>
          }
        />
      )}

      <hr
        className={twMerge(
          "md:hidden opacity-50 mix-blend-hard-light",
          primaryItem.textTheme !== "light" && "mix-blend-overlay",
          secondaryItems.length === 0 && "hidden",
        )}
      />

      <PrimitiveHeroUnit.CardList>
        {secondaryItems.map((item, index) => {
          const sharedProps = {
            key: `${item.content.id}_${index}`,
            events: [
              {
                event: "Link Clicked",
                properties: {
                  title: getOriginalItemTitle(item),
                  sourceElement: HERO_UNIT_ITEM_TYPES.SECONDARY,
                  sourceName: SECTIONS.HERO_UNIT,
                  sourcePage: PAGES.HOME,
                  sourcePageType: PAGES.HOME,
                  url: heroUnit.url,
                  ...getUtmParamsFromQueryParams(router.query),
                },
              },
            ],
          };
          if (item.contentType === CONTENT_TYPES.CUSTOM_LINK) {
            return (
              // eslint-disable-next-line react/jsx-key
              <Link href={item.content.destinationUrl} {...sharedProps}>
                <PrimitiveHeroUnit.SecondaryCard
                  title={item.title}
                  description={item.description}
                  image={item.image}
                />
              </Link>
            );
          }
          return (
            // eslint-disable-next-line react/jsx-key
            <SecondaryItemCardWithLink
              contentLinkAsBase={getRoutePrefixByContentType(item.contentType)}
              contentLinkHrefBase={`${getRoutePrefixByContentType(
                item.contentType,
              )}/[...identity]`}
              id={item.content.id}
              slug={item.content.slug}
              linkParams={{
                sourceElement: HERO_UNIT_ITEM_TYPES.SECONDARY,
                sourceName: SECTIONS.HERO_UNIT,
                sourcePage: PAGES.HOME,
                sourcePageType: PAGES.HOME,
              }}
              title={item.title}
              description={item.description}
              image={item.image}
              {...sharedProps}
            />
          );
        })}
      </PrimitiveHeroUnit.CardList>
    </PrimitiveHeroUnit.Root>
  );
}
